import { useEffect, useState } from 'react'
import { SITE } from '../lib/seo'
import { docPath, type DocDetail } from '../lib/types'
import { Button } from './ui'

/**
 * Copy this page's link.
 *
 * The canonical URL, never `location.href`: a reader who arrived on `/d/126` would
 * otherwise pass on the address that redirects instead of the one the page lives at,
 * and the hash of whichever section they happened to scroll to.
 *
 * Mount this with `key={doc.id}` so the confirmation cannot outlive the doc it
 * belongs to.
 */
export function ShareDoc({ doc }: { doc: DocDetail }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const t = setTimeout(() => setState('idle'), 2000)
    return () => clearTimeout(t)
  }, [state])

  const share = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE}${docPath(doc)}`)
      setState('copied')
    } catch {
      // No clipboard permission, or a context the browser does not consider secure.
      // Say so — a button that reports success it did not have is worse than useless,
      // because the reader pastes whatever was in the clipboard before.
      setState('failed')
    }
  }

  const internal = doc.visibility === 'internal'

  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => void share()}
      title={
        internal
          ? 'Copy link — this page is internal, so only staff can open it'
          : 'Copy link to this page'
      }
    >
      <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {state === 'copied' ? (
          <path d="M20 6L9 17l-5-5" />
        ) : (
          <>
            <path d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.5 1.5" />
            <path d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.5-1.5" />
          </>
        )}
      </svg>
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : 'Share'}
    </Button>
  )
}
